import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { ContactTabSectionHeadingProps } from '~/scenes/PeopleModule/components/Tabs/ContactTab/components/ContactTabSectionHeading';
import { AppDispatch } from '~/store';
import { isValidOptionalPhoneNumber } from '~/utils/helper';

import {
  Country,
  EmpOtherContactType,
  deleteEmpOtherContactData,
  putEmpOtherContactData,
} from './data';

export type IFormInput = {
  fEmpPhoneOther: string;
  fEmpEmailOther: string;
  fEmpStreetAddress1: string;
  fEmpStreetAddress2: string;
  fEmpPostalCode: string;
  fEmpCity: string;
  fEmpState: string;
  fEmpCountry: string;
};

export type useOtherContractOutput = {
  form: UseFormReturn<IFormInput>;
  actions: Pick<
    ContactTabSectionHeadingProps,
    'onEdit' | 'onSave' | 'onCancel' | 'onDelete'
  >;
  readMode: boolean;
  empData: EmpOtherContactType;
  countriesList: Country[];
  isLoading: boolean;
};

export const useOtherContract = (): useOtherContractOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();
  const confirm = useConfirmDialog();

  const { getEnums } = useAllEnums();
  const countriesList = useMemo(() => {
    return getEnums(EnumListKey.COUNTRY);
  }, [getEnums]);

  const [readMode, setReadMode] = useState(true);

  const {
    employee: { empOtherContact: empData, empBasicData },
    isLoading,
  } = useEmployee();

  const emptyFormData: IFormInput = {
    fEmpPhoneOther: '',
    fEmpEmailOther: '',
    fEmpStreetAddress1: '',
    fEmpStreetAddress2: '',
    fEmpPostalCode: '',
    fEmpCity: '',
    fEmpState: '',
    fEmpCountry: '',
  };

  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpPhoneOther: yup
        .string()
        .defined()
        .test(
          'phoneNumberValidation',
          t('phoneInvalid'),
          isValidOptionalPhoneNumber,
        ),
      fEmpEmailOther: yup.string().email(t('laValidEmail')).defined(),
      fEmpStreetAddress1: yup.string().required(t('laFillManFields')),
      fEmpStreetAddress2: yup.string(),
      fEmpPostalCode: yup.string().required(t('laFillManFields')),
      fEmpCity: yup.string().required(t('laFillManFields')),
      fEmpState: yup.string(),
      fEmpCountry: yup.string().required(t('laFillManFields')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<IFormInput>({
    resolver,
    defaultValues: { ...emptyFormData, ...empData },
  });
  const { handleSubmit, reset } = form;

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset(emptyFormData);
    } else {
      reset();
    }
    setReadMode(true);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpPhoneOther: data.fEmpPhoneOther,
        fEmpEmailOther: data.fEmpEmailOther,
        fEmpStreetAddress1: data.fEmpStreetAddress1,
        fEmpStreetAddress2: data.fEmpStreetAddress2,
        fEmpPostalCode: data.fEmpPostalCode,
        fEmpState: data.fEmpState,
        fEmpCity: data.fEmpCity,
        fEmpCountry: data.fEmpCountry,
      };
      (async () => {
        try {
          await putEmpOtherContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const deleteContact = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpOtherContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          handleFormReset(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  return {
    form,
    actions: {
      onDelete: deleteContact,
      onSave: onSubmit,
      onCancel: () => handleFormReset(false),
      onEdit: () => setReadMode(false),
    },
    countriesList,
    readMode,
    empData,
    isLoading,
  };
};
